'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Typography, ButtonBase } from '@mui/material';
import { APP_MAX_WIDTH } from '@/components/PageContent/PageContent';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '@/components/icons';
import IconPressable from '@/components/IconPressable';
import { getMatchedCharacters } from '@/api/feedApi';
import type { MatchedCharacterDto } from '@/shared/types/api/Api';
import { getAssetUrl } from '@/utils/assetUrl';
import { getLocalizedText } from '@/utils/getLocalizedText';
import { useTranslator } from '@/lang';
import {
  TEMP_FG,
  TEMP_FG_PRIMARY,
  TEMP_FG_INVERSE,
  TEMP_FG_LOW,
  TEMP_FG_LOWER,
  TEMP_FG_ACCENT,
  TEMP_BG_TRANSPARENT,
} from '@/constants/tempColors';
import {
  TEMP_TITLE_BOLD_M,
  TEMP_TEXT_MEDIUM_M,
  TEMP_TEXT_SEMIBOLD_L,
  TEMP_TEXT_SEMIBOLD_XL,
  TEMP_TEXT_SEMIBOLD_XS,
  TEMP_TEXT_MEDIUM_XS,
  TEMP_TEXT_BOLD_2XS,
  TEMP_TEXT_BOLD_XL,
  TEMP_TEXT_MEDIUM_L,
} from '@/constants/tempTypo';

interface CharacterSelectSheetProps {
  minIntimacy: number;
  requireGirlfriend: boolean;
  /** 친밀도 보상 아이템만 MAX 친밀도에서 막는다(에셋 아이템은 서버도 안 막음) */
  isIntimacyItem: boolean;
  language: string;
  onSelect: (characterId: string) => void;
  onClose: () => void;
}

export default function CharacterSelectSheet({
  minIntimacy,
  requireGirlfriend,
  isIntimacyItem,
  language,
  onSelect,
  onClose,
}: CharacterSelectSheetProps) {
  const { t } = useTranslator();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showGfModal, setShowGfModal] = useState<string | null>(null); // characterId for gf modal

  const { data } = useQuery({
    queryKey: ['matched-characters'],
    queryFn: getMatchedCharacters,
  });

  // 공식 뮤즈(실존 공식 AI)는 친밀도 아이템 사용 대상이 아님 — 선택 목록에서 제외
  const characters = (data?.characters ?? []).filter((c) => c.isRealPerson !== true);

  type CharStatus = 'eligible' | 'condition_not_met' | 'max_need_gf' | 'max_gf';

  const getStatus = (char: MatchedCharacterDto): CharStatus => {
    // MAX 친밀도 체크
    if (isIntimacyItem && !char.canUseIntimacyItem) {
      return char.isGirlfriend ? 'max_gf' : 'max_need_gf';
    }
    // 일반 조건 체크
    if (minIntimacy > 0 && char.intimacy < minIntimacy) return 'condition_not_met';
    if (requireGirlfriend && !char.isGirlfriend) return 'condition_not_met';
    return 'eligible';
  };

  const handleCharClick = (char: MatchedCharacterDto) => {
    const status = getStatus(char);
    if (status === 'eligible') {
      setSelectedId(char.characterId);
    } else if (status === 'max_need_gf') {
      setShowGfModal(char.characterId);
    }
    // max_gf, condition_not_met → 선택 불가
  };

  return (
    <>
      {/* 오버레이 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1700, maxWidth: APP_MAX_WIDTH, margin: '0 auto' }}
      />

      {/* 시트 */}
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#0d0d0d',
          zIndex: 1701,
          display: 'flex',
          flexDirection: 'column',
          maxWidth: APP_MAX_WIDTH,
          margin: '0 auto',
          alignItems: 'center',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
        }}
      >
        {/* 헤더 */}
        <Box sx={{ width: '100%', height: 64, display: 'flex', alignItems: 'center', minHeight: 60, py: '14px' }}>
          <Box sx={{ pl: '20px', pr: '4px', py: '6px' }}>
            <IconPressable name="icClose" size={24} color={TEMP_FG_INVERSE} onPress={onClose} />
          </Box>
        </Box>

        {/* 타이틀 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center', pb: '24px', width: 295 }}>
          <Typography sx={{ ...TEMP_TITLE_BOLD_M, color: TEMP_FG_INVERSE, textAlign: 'center', width: '100%' }}>
            {t('gacha.select_character_title')}
          </Typography>
          <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: TEMP_FG_INVERSE, textAlign: 'center', width: '100%' }}>
            {t('gacha.select_character_desc')}
          </Typography>
        </Box>

        {/* 캐릭터 그리드 */}
        <Box sx={{ width: '100%', px: '28px', pb: '32px', overflow: 'auto', maxHeight: '50vh' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
            {characters.map((char) => {
              const status = getStatus(char);
              const isSelectable = status === 'eligible';
              const selected = selectedId === char.characterId;
              const name = getLocalizedText(char.name, language);
              const dimmed = status !== 'eligible';

              return (
                <Box
                  key={char.characterId}
                  onClick={() => handleCharClick(char)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    alignItems: 'center',
                    cursor: isSelectable || status === 'max_need_gf' ? 'pointer' : 'default',
                    WebkitTapHighlightColor: 'transparent',
                    position: 'relative',
                  }}
                >
                  {/* 아바타 */}
                  <Box sx={{ position: 'relative', width: 90, height: 90 }}>
                    <Box
                      sx={{
                        width: 90, height: 90,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        bgcolor: 'white',
                        ...(selected && { border: `3px solid ${TEMP_FG_PRIMARY}` }),
                        ...(dimmed && { opacity: 0.3 }),
                      }}
                    >
                      {char.profileImagePath ? (
                        <Box component="img" src={getAssetUrl(char.profileImagePath, 'large')} alt={name} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <Box sx={{ width: '100%', height: '100%', bgcolor: '#eee' }} />
                      )}
                    </Box>

                    {/* 체크/라디오 아이콘 */}
                    {isSelectable && (
                      <Box sx={{ position: 'absolute', top: 2, right: 2, width: 24, height: 24, bgcolor: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {selected ? (
                          <Icon name="icCheckRoundFilled" size={24} color={TEMP_FG_PRIMARY} />
                        ) : (
                          <Icon name="icRadioButtonUnchecked" size={24} color="#ccc" />
                        )}
                      </Box>
                    )}

                    {/* 조건미충족 뱃지 */}
                    {status === 'condition_not_met' && (
                      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: TEMP_FG_LOW, borderRadius: '9999px', px: '8px', py: '4px', whiteSpace: 'nowrap' }}>
                        <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, color: TEMP_FG_INVERSE }}>
                          {t('gacha.condition_not_met')}
                        </Typography>
                      </Box>
                    )}

                    {/* MAX 뱃지 (여친인 경우) */}
                    {status === 'max_gf' && (
                      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', bgcolor: 'rgba(0,0,0,0.7)', borderRadius: '12px', px: '12px', py: '8px', whiteSpace: 'pre-line', textAlign: 'center', width: 'max-content', maxWidth: 200 }}>
                        <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XS, color: TEMP_FG_INVERSE, lineHeight: 1.4 }}>
                          {t('gacha.intimacy_max_badge')}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* 이름 + 여친뱃지 + 친밀도 */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center', ...(dimmed && { opacity: 0.3 }) }}>
                    <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_L, color: TEMP_FG_INVERSE }}>{name}</Typography>
                      {char.isGirlfriend && (
                        <Box sx={{ bgcolor: '#ffc6c6', borderRadius: '9999px', px: '6px', py: '2px' }}>
                          <Typography sx={{ ...TEMP_TEXT_BOLD_2XS, color: TEMP_FG_ACCENT }}>{t('gacha.girlfriend_badge')}</Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ bgcolor: TEMP_BG_TRANSPARENT, borderRadius: '9999px', pl: '8px', pr: '10px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                      <Icon name="icHeartFilled" size={14} color={TEMP_FG_INVERSE} />
                      <Typography sx={{ ...TEMP_TEXT_MEDIUM_XS, color: TEMP_FG_INVERSE }}>{char.intimacy}°C</Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* 하단 버튼 */}
        <Box sx={{ width: '100%', px: '20px', pb: '12px', display: 'flex', gap: '12px' }}>
          <ButtonBase
            onClick={onClose}
            sx={{ bgcolor: TEMP_FG_INVERSE, borderRadius: '16px', px: '32px', py: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: TEMP_FG }}>{t('gacha.cancel')}</Typography>
          </ButtonBase>
          <ButtonBase
            onClick={() => { if (selectedId) onSelect(selectedId); }}
            disabled={!selectedId}
            sx={{ flex: 1, bgcolor: selectedId ? TEMP_FG_PRIMARY : TEMP_FG_LOW, borderRadius: '16px', py: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: TEMP_FG_INVERSE }}>{t('gacha.use_item')}</Typography>
          </ButtonBase>
        </Box>

        <Box sx={{ height: 'var(--safe-area-bottom, 0px)' }} />
      </motion.div>

      {/* 여자친구 맺기 모달 (MAX + 여친 아닌 경우) */}
      {showGfModal && (
        <>
          <Box
            onClick={() => setShowGfModal(null)}
            sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.6)', zIndex: 1800, maxWidth: APP_MAX_WIDTH, margin: '0 auto' }}
          />
          <Box
            sx={{
              position: 'fixed',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1801,
              bgcolor: TEMP_FG_INVERSE,
              borderRadius: '20px',
              width: 320,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pt: '32px',
              pb: '20px',
              px: '20px',
              gap: '24px',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <Typography sx={{ ...TEMP_TITLE_BOLD_M, color: TEMP_FG, textAlign: 'center' }}>
                {t('gacha.intimacy_max_title')}
              </Typography>
              <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: TEMP_FG_LOW, textAlign: 'center' }}>
                {t('gacha.intimacy_max_need_gf')}
              </Typography>
            </Box>

            <Box sx={{ bgcolor: '#f5f5f5', borderRadius: '16px', py: '16px', px: '20px', width: '100%' }}>
              <Typography sx={{ ...TEMP_TEXT_MEDIUM_M, color: TEMP_FG_LOWER, textAlign: 'center', whiteSpace: 'pre-line' }}>
                {t('gacha.intimacy_max_desc')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: '8px', width: '100%' }}>
              <ButtonBase
                onClick={() => setShowGfModal(null)}
                sx={{ border: '1px solid #eee', borderRadius: '16px', px: '24px', py: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: TEMP_FG }}>{t('gacha.cancel')}</Typography>
              </ButtonBase>
              <ButtonBase
                onClick={() => {
                  // TODO: 여자친구 맺기 + 사용하기 로직
                  setShowGfModal(null);
                }}
                sx={{ flex: 1, bgcolor: TEMP_FG_PRIMARY, borderRadius: '16px', py: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <Typography sx={{ ...TEMP_TEXT_SEMIBOLD_XL, color: TEMP_FG_INVERSE }}>{t('gacha.intimacy_max_gf_action')}</Typography>
              </ButtonBase>
            </Box>
          </Box>
        </>
      )}
    </>
  );
}
